import { useState, useCallback } from "react";

/**
 * useCanvasWorkflow — manages state for canvas-based workflows (brief, wrap_report).
 *
 * Encapsulates:
 *  - canvasUpdates: list of canvases created by the agent
 *  - newCanvasData: live streamed markdown
 *  - canvasLoading: initial loading state
 *  - selectedCanvas: currently active canvas (for export)
 */
export default function useCanvasWorkflow() {
    const [canvasUpdates, setCanvasUpdates] = useState([]);
    const [newCanvasData, setNewCanvasData] = useState(null);
    const [canvasLoading, setCanvasLoading] = useState(false);
    const [selectedCanvas, setSelectedCanvas] = useState(null);

    /**
     * Handles a new canvas_id tool_result — creates or updates a canvas entry.
     */
    const handleCanvasToolResult = useCallback(
        (canvasId, canvasName, totalCount) => {
            setCanvasUpdates((prev) => {
                const existingIndex = prev.findIndex((c) => c.id === canvasId);
                const entry = {
                    id: canvasId,
                    name: canvasName || `Canvas ${prev.length + 1}`,
                    timestamp: Date.now(),
                };
                if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = entry;
                    return updated;
                }
                return [...prev, entry];
            });
        },
        [],
    );

    /**
     * Handles streaming canvas content snapshots.
     */
    const handleCanvasSnapshot = useCallback((canvasContent) => {
        setNewCanvasData(canvasContent);
    }, []);

    /**
     * Loads canvases from a reconnected thread.
     */
    const loadCanvasesFromThread = useCallback((canvases) => {
        if (canvases && canvases.length > 0) {
            setCanvasUpdates(
                canvases.map((c, index) => ({
                    id: c.id,
                    name: c.name || `Canvas ${index + 1}`,
                    timestamp: Date.now(),
                })),
            );
        }
    }, []);

    /**
     * Resets all canvas state (called on thread switch).
     */
    const reset = useCallback(() => {
        setCanvasUpdates([]);
        setNewCanvasData(null);
        setCanvasLoading(false);
        setSelectedCanvas(null);
    }, []);

    return {
        canvasUpdates,
        newCanvasData,
        canvasLoading,
        selectedCanvas,
        setCanvasLoading,
        setSelectedCanvas,
        handleCanvasToolResult,
        handleCanvasSnapshot,
        loadCanvasesFromThread,
        reset,
    };
}
